import type { NodeProps } from "reactflow";

export type PassageMouthNodeData = {
  /** Human-readable destination zone name (for tooltip). */
  destinationLabel: string;
  /** Direction the passage points (e.g. "north"). May be empty for unnamed passages. */
  direction: string;
  /** Edge kind — drives the marker glyph (single chevron vs double-headed arrow). */
  kind: "passage" | "bidirectional-passage";
  /**
   * True if this zone is the passage's source endpoint (one-way exit from here),
   * false if this zone is the target (one-way entry from elsewhere). Ignored
   * for bidirectional-passage where the distinction doesn't apply.
   */
  isOutgoing: boolean;
  /** Edge id — surfaced for test selectors + future click handlers. */
  edgeId: string;
};

/**
 * Passage-mouth marker on the spatial debug grid (LAF-215).
 *
 * Renders at the per-zone mouth coordinate (`mouthA` for source-side, `mouthB`
 * for target-side). Visually distinct from entity tokens (rounded circles,
 * blue/red/gray) and blocked tiles (stone-colored squares) via an amber
 * square-with-glyph treatment. Hover tooltip surfaces destination zone +
 * direction.
 */
export const PassageMouthNode = ({ data }: NodeProps<PassageMouthNodeData>) => {
  const { destinationLabel, direction, kind, isOutgoing, edgeId } = data;
  const isBidirectional = kind === "bidirectional-passage";

  // Tooltip wording:
  // - bidirectional: "↔ Kitchen (north)" — two-way
  // - one-way outgoing: "→ Kitchen (north)" — exit
  // - one-way incoming: "← Kitchen (south)" — entry (no walk-back)
  const arrow = isBidirectional ? "↔" : isOutgoing ? "→" : "←";
  const directionSuffix = direction ? ` (${direction})` : "";
  const tooltip = `${arrow} ${destinationLabel}${directionSuffix}`;

  // Glyph mirrors the arrow used in the tooltip so the marker reads at a glance.
  const glyph = isBidirectional ? "⇄" : isOutgoing ? "▸" : "◂";

  return (
    <div
      className="flex h-10 w-10 items-center justify-center rounded border border-amber-700 bg-amber-300 font-bold text-amber-900 text-base shadow-sm"
      data-edge-id={edgeId}
      data-passage-mouth={isOutgoing ? "outgoing" : "incoming"}
      title={tooltip}
    >
      {glyph}
    </div>
  );
};
